/**
 * @fileoverview The pricing page's subtitle, derived from the plan catalog.
 *
 * It used to be a sentence: "Every optimizer and every constraint is on the free
 * plan. Professional adds bigger fleets, multi-day routes, and real road
 * distances." That was false. `free.features` turns five capabilities off —
 * mixed vehicle types, resupply, multi-day, locked assignments and road
 * distances — and the comparison table a few inches below it rendered crosses
 * for four of them on the same screen. The sentence even contradicted itself:
 * it claimed free had every constraint, then named two that Professional adds.
 *
 * It went wrong the way written-down claims do. Nobody edited it to make it
 * false; the catalog moved and the sentence couldn't notice. So this doesn't
 * replace it with a better sentence — it states only what the catalog says, and
 * the list of what Professional adds is computed from the difference between
 * the two plans rather than remembered.
 *
 * What it cannot check: that the two optimizers and time windows are on every
 * plan. Those are real entitlement keys in the backend registry
 * (`model_type:vehicle-routing`, `model_type:pickup-delivery`, `time_windows`),
 * but `tier_features()` publishes only what a tier DECLARES, and no tier
 * declares them — so `GET /plans` never mentions them and the front end cannot
 * confirm them. Rather than assert them anyway, this subtitle doesn't mention
 * them at all. (See planComparisonRows.js for the same problem in the table.)
 */

import { findPlanInCatalog } from 'functions/entitlements/featureEntitlements';

/**
 * Used whenever the catalog can't answer — in flight, failed, or missing a cap.
 * Deliberately makes no checkable claim, so it cannot become the next false
 * sentence. A pricing page mid-load should say less, not guess.
 */
export const PRICING_SUBTITLE_FALLBACK =
  'Start on the free plan and upgrade when your fleet or your constraints outgrow it.';

/**
 * Prose for each capability a paid plan can add, in the order they read best.
 *
 * A key the catalog reports that isn't here is left OUT of the sentence. That
 * under-claims, which is the safe direction: a capability we forgot to name
 * costs us a line of marketing, while one we name and don't deliver is the bug
 * this file exists to prevent.
 *
 * The wording deliberately matches PROFESSIONAL_ONLY_PHRASES in pricingCro.test.js,
 * which checks that every capability the header names is one the catalog really
 * withholds from free. Phrasing these differently would not fail that test — it
 * would quietly stop covering them, which is worse.
 */
const FEATURE_PROSE = {
  road_network_costs: 'real road distances',
  multi_day: 'multi-day routes',
  resupply: 'mid-route resupply',
  heterogeneous_travel_costs: 'travel costs for each vehicle type',
  fixed_assignments: 'locking a stop to a vehicle',
};

const finiteOrNull = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** "a", "a and b", "a, b and c" — no serial comma, matching the site's voice. */
function listSentence(items) {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Build the subtitle from a GET /plans catalog.
 *
 * @param {Array<object>|null|undefined} catalog
 * @returns {string} Always a usable sentence; never an empty string.
 */
export function buildPricingSubtitle(catalog) {
  const free = findPlanInCatalog(catalog, 'free');
  const professional = findPlanInCatalog(catalog, 'professional');
  if (!free || !professional) return PRICING_SUBTITLE_FALLBACK;

  const freeStops = finiteOrNull(free.limits?.locations?.maxRows);
  const freeVehicles = finiteOrNull(free.limits?.vehicles?.maxRows);
  const proStops = finiteOrNull(professional.limits?.locations?.maxRows);
  const proVehicles = finiteOrNull(professional.limits?.vehicles?.maxRows);
  if (freeStops === null || freeVehicles === null || proStops === null || proVehicles === null) {
    return PRICING_SUBTITLE_FALLBACK;
  }

  // What Professional has and free doesn't — computed, not remembered. A
  // capability granted to free tomorrow drops out of this list by itself.
  const freeFeatures = free.features || {};
  const proFeatures = professional.features || {};
  const added = Object.keys(FEATURE_PROSE)
    .filter((key) => proFeatures[key] === true && freeFeatures[key] !== true)
    .map((key) => FEATURE_PROSE[key]);

  const base = `The free plan solves up to ${freeStops} stops and ${freeVehicles} vehicles. Professional raises that to ${proStops} and ${proVehicles}`;
  return added.length > 0 ? `${base}, and adds ${listSentence(added)}.` : `${base}.`;
}

export default buildPricingSubtitle;
